// Programmatic SEO: City × Topic landing pages
// Routes: /:topicSlug-home-tuition-:citySlug e.g. /cbse-home-tuition-jabalpur
//
// Topic = Board / Subject / Exam / Class group
// Each page is uniquely composed at render time from the topic + city data,
// emitting LocalBusiness + Service + Course + FAQPage + BreadcrumbList JSON-LD.

import { CITIES } from "./siteData";

// 12 high-intent topics × 3 cities = 36 programmatic pages
export const SEO_TOPICS = {
 // Boards
 "cbse": {
 kind: "board",
 label: "CBSE",
 formal: "CBSE (Central Board of Secondary Education)",
 urlVerb: "home-tuition",
 h1Verb: "Home Tuition",
 },
 "icse": {
 kind: "board",
 label: "ICSE & ISC",
 formal: "ICSE & ISC (Council for the Indian School Certificate Examinations)",
 urlVerb: "home-tuition",
 h1Verb: "Home Tuition",
 },
 "ib": {
 kind: "board",
 label: "IB",
 formal: "IB (International Baccalaureate)",
 urlVerb: "home-tuition",
 h1Verb: "Home Tuition",
 },
 "igcse": {
 kind: "board",
 label: "IGCSE",
 formal: "IGCSE (Cambridge International)",
 urlVerb: "home-tuition",
 h1Verb: "Home Tuition",
 },
 "state-board": {
 kind: "board",
 label: "State Board",
 formal: "State Board (MP / Maharashtra)",
 urlVerb: "home-tuition",
 h1Verb: "Home Tuition",
 },

 // Subjects
 "maths": {
 kind: "subject",
 label: "Maths",
 formal: "Mathematics",
 urlVerb: "home-tutor",
 h1Verb: "Home Tutor",
 },
 "science": {
 kind: "subject",
 label: "Science",
 formal: "Science (Physics, Chemistry, Biology)",
 urlVerb: "home-tutor",
 h1Verb: "Home Tutor",
 },
 "english": {
 kind: "subject",
 label: "English",
 formal: "English Language & Literature",
 urlVerb: "home-tutor",
 h1Verb: "Home Tutor",
 },

 // Exams
 "neet": {
 kind: "exam",
 label: "NEET",
 formal: "NEET (Medical Entrance)",
 urlVerb: "coaching",
 h1Verb: "Coaching",
 },
 "jee": {
 kind: "exam",
 label: "JEE",
 formal: "JEE Main & Advanced",
 urlVerb: "coaching",
 h1Verb: "Coaching",
 },

 // Class groups
 "class-10": {
 kind: "class",
 label: "Class 10",
 formal: "Class 10 (Board Year)",
 urlVerb: "home-tuition",
 h1Verb: "Home Tuition",
 },
 "class-11-12": {
 kind: "class",
 label: "Class 11 & 12",
 formal: "Class 11 & 12 (Senior Secondary)",
 urlVerb: "home-tuition",
 h1Verb: "Home Tuition",
 },
};

export const SEO_CITIES = ["jabalpur", "bhopal", "nagpur"];

// Helper: returns the programmatic SEO data for a given (topic, city).
export function getCityTopicData(topicSlug, citySlug) {
 const topic = SEO_TOPICS[topicSlug];
 const city = CITIES[citySlug];
 if (!topic || !city) return null;

 const cityName = city.name;
 const cityLabel = `${topic.label} ${topic.h1Verb} in ${cityName}`;
 const path = `/${topicSlug}-${topic.urlVerb}-${citySlug}`;

 // SEO copy
 const seoTitle = `Best ${topic.label} ${topic.h1Verb} in ${cityName} — 1:1 Verified Tutors | Anand Tutorials`;
 const seoDescription = `Top-rated ${topic.label} ${topic.h1Verb.toLowerCase()} in ${cityName}. 1:1 verified tutors, flexible timings, free demo class. ${topic.formal} specialists since 2013. Call +91 99933 05574.`;
 const keywords = [
 `${topic.label} ${topic.h1Verb.toLowerCase()} in ${cityName}`,
 `${topic.label} home tutor ${cityName}`,
 `home tuition for ${topic.label} ${cityName}`,
 `best ${topic.label} tutor ${cityName}`,
 `${topic.label} coaching ${cityName}`,
 `Anand Tutorials ${cityName}`,
 `1:1 ${topic.label} tutor ${cityName}`,
 ].join(", ");

 return {
 slug: path,
 topic,
 city,
 cityName,
 cityLabel,
 seoTitle,
 seoDescription,
 keywords,
 h1: `${topic.label} ${topic.h1Verb} in ${cityName}`,
 eyebrow: `Local ${topic.kind === "exam" ? "Coaching" : "Tutors"} · ${cityName}`,
 // Hero copy
 intro: `Looking for the best ${topic.label} ${topic.h1Verb.toLowerCase()} in ${cityName}? Anand Tutorials matches your child with a verified ${topic.formal} specialist tutor — at home or live online — across every ${cityName} locality. We've been ${cityName}'s most trusted name in 1-on-1 academic tutoring since 2013.`,
 // Local content blocks
 whyLocal: [
 `Verified tutors who personally serve ${cityName} households — they know your child's school, syllabus pace and exam expectations.`,
 `Coverage across all major ${cityName} localities (see area list below) — a tutor reaches your home within 24-48 hours of booking.`,
 `Flexibility for ${cityName} school timings — early-morning, post-school, weekend slots all available.`,
 `${topic.kind === "board" ? `Specialist for ${topic.label} pattern, marking scheme and exam blueprint — not generic teaching.` : topic.kind === "subject" ? `${topic.label} specialists drawn from ${cityName}'s top schools and tutoring institutes — not freelance generalists.` : topic.kind === "exam" ? `${topic.label} mentors with proven exam-cracker backgrounds — including AIIMS, IIT and PGIMER alumni.` : `Curriculum experts who know the exact Class-level pacing for ${cityName} schools (DPS, Christ Church, Kendriya Vidyalaya, MGM and others).`}`,
 ],
 // Top localities
 topAreas: city.areas || [],
 // FAQs unique per (topic, city)
 faqs: [
 [
 `Where in ${cityName} do you offer ${topic.label} ${topic.h1Verb.toLowerCase()}?`,
 `We provide ${topic.label} ${topic.h1Verb.toLowerCase()} across all major ${cityName} localities including ${(city.areas || []).slice(0, 5).join(", ")} and 15+ other neighbourhoods. A tutor can reach your home within 24-48 hours of booking.`,
 ],
 [
 `How much does ${topic.label} ${topic.h1Verb.toLowerCase()} cost in ${cityName}?`,
 `Tuition fees depend on multiple factors such as class, subject, board, location, learning goals, and frequency of classes. Contact Anand Tutorials for a personalized recommendation and tutor matching — your free demo class is included.`,
 ],
 [
 `Is the demo class really free for ${cityName} families?`,
 `Yes — 100% free, no card required. We send a verified ${topic.label} tutor to your home in ${cityName} for one full demo class. Continue only if you love the experience.`,
 ],
 [
 `Can I get a female tutor in ${cityName}?`,
 `Absolutely. Female tutor availability is standard for us in ${cityName} — request at the time of booking, no extra charge.`,
 ],
 [
 `Do you also offer online ${topic.label} ${topic.h1Verb.toLowerCase()}?`,
 `Yes — many ${cityName} families choose our 1:1 live online classes for flexibility and access to our top tutors regardless of locality. Both home and online options are available.`,
 ],
 ],
 };
}

// All 36 page slugs (used by sitemap and link helpers)
export function getAllCityTopicSlugs() {
 const slugs = [];
 for (const topicSlug of Object.keys(SEO_TOPICS)) {
 for (const citySlug of SEO_CITIES) {
 const t = SEO_TOPICS[topicSlug];
 slugs.push(`/${topicSlug}-${t.urlVerb}-${citySlug}`);
 }
 }
 return slugs;
}
